<template>
  <div
    v-if="!!activeJob"
    class="i-career-overview"
  >
    <iBaseHeader
      :highlight-text="activeJob.title"
      :font-size-highlighted-text="headerSize"
      text=""
      align="start"
    />
    <div
      v-if="GlobalFunctions.isExtendedRecommendedJobType(activeJob)"
      class="sub"
    >
      <div
        class="badge"
        :style="{ backgroundColor: activeJob.color }"
      >
        {{ badgeText }}
      </div>
      <template v-if="showJobFeedback && activeJob">
        <div>{{ recommendationsCount }} Users</div>
        <div
          v-if="isTabletAndUp"
          class="circle"
        ></div>
        <div v-if="userStore.isTierComprehensiveOrHigher">
          {{ activeJob.characteristics.satisfaction / 10 }} out of 10 people recommend this career
        </div>
      </template>
    </div>
    <div
      v-if="!!activeJob?.overview"
      class="overview"
    >
      {{ activeJob.overview }}
    </div>
    <iCareerActions />
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue"
import { useUserStore } from "@/stores/user"
import { useReportStore } from "@/stores/report"
import { breakpointSm, breakpointMd } from "@/styles/variables"

import useIsScreenSize from "@/composables/useIsScreenSize"
import useRandomNumber from "@/composables/useRandomNumber"
import { GlobalFunctions } from "@/helpers/globalFunctions"

import iBaseHeader from "@/components/base/BaseHeader.vue"
import iCareerActions from "@/components/report/careers/CareersActions.vue"

// STORES AND COMPOSABLES
const userStore = useUserStore()
const reportStore = useReportStore()
const isNotMobile = useIsScreenSize(breakpointSm)
const isTabletAndUp = useIsScreenSize(breakpointMd)

// COMPUTED
const headerSize = computed(() => (isNotMobile.value ? "36px" : "32px"))
const activeJob = computed(() => reportStore.getActiveJob)
const showJobFeedback = computed(() => {
  return (
    GlobalFunctions.isExtendedRecommendedJobType(activeJob.value) &&
    !!activeJob.value?.id &&
    !!activeJob.value?.title &&
    !activeJob.value?.isCurrentJob &&
    !activeJob.value?.isActivelyExploringJob &&
    !activeJob.value?.isSampleJob
  )
})
const badgeText = computed(() => {
  return GlobalFunctions.isExtendedRecommendedJobType(activeJob.value)
    ? showJobFeedback.value
      ? `You are a ${activeJob.value?.badge} match`
      : `${activeJob.value?.badge} Career`
    : ""
})
const recommendationsCount = computed(() => {
  const randomValue = useRandomNumber(`${userStore.getFullName} ${reportStore.activeJobId}`, 80, 100).value
  const activeJobRecCount = activeJob.value?.statistics?.recommendationsCount || 0
  return activeJobRecCount < 100 ? randomValue : activeJobRecCount
})
</script>

<style lang="scss" scoped>
.i-career-overview {
  .sub {
    @include flex(column, start, stretch, $gap: 10px);
    @include text-sm;

    font-weight: 500;
    color: $pickle-bluewood900;
    margin: 8px 0 10px;

    @media screen and (min-width: $breakpoint-sm) {
      margin: 15px 0;
    }

    @media screen and (min-width: $breakpoint-md) {
      @include flex(row, start, center, wrap, $gap: 5px 15px);
    }

    .badge {
      @include border-radius-lg;

      white-space: nowrap;
      padding: 3px 12px;
      color: $white;
      width: max-content;
      margin-bottom: 5px;
    }

    .circle:not(:last-child) {
      flex: 0 0 6px;
      width: 6px;
      height: 6px;

      @include border-radius-rounded;

      background-color: #676474;
    }
  }

  .overview {
    color: $pickle-bluewood900;

    @include text-sm;

    font-weight: 500;
    margin-top: 10px;

    @media screen and (min-width: $breakpoint-sm) {
      @include text-md;
    }
  }
}
</style>
